<mat-card style="width: min-content; max-width: 100%;">
  <mat-card-header>
    <div class="g-just-flex flex-between w-100 flex-wrap gap-20">
      <div class="left">
        <mat-card-title>Supply Stock</mat-card-title>
        <mat-card-subtitle>SupplyDelivery items and remaining stock</mat-card-subtitle>
      </div>
      <div class="right gap-10 g-just-flex" style="flex-wrap: wrap; flex: 1 1 auto; justify-content: flex-end;">
        @if (canAdd$ | async) {
        <button mat-flat-button (click)="openAddDialog()">
          <mat-icon>add</mat-icon>
          Add supply item
        </button>
        } @else {
        <button mat-flat-button disabled>
          <mat-icon>lock</mat-icon>
          Add supply item
        </button>
        }
      </div>
    </div>
  </mat-card-header>

  <mat-card-content class="my-12">
    <mat-divider></mat-divider>

    <app-table-header 
      searchBoxTitle="Search by item code (tag)..." 
      (searchCriteriaChanged)="onSearch($event)">
    </app-table-header>

    @if (loading$ | async) {
    <div class="g-just-flex" style="justify-content: center; padding: 24px;">
      <mat-progress-spinner mode="indeterminate"></mat-progress-spinner>
    </div>
    } @else {
    <div class="table-or-empty" style="background-color: var(--mat-sys-surface)">
      @if (tableDataSource.data.length === 0) {
      <div class="g-just-flex flex-column align-items-center py-40" style="text-align:center;">
        <app-empty-state title="No supply items" subtitle="No SupplyDelivery records found." />
      </div>
      } @else {
      <div class="supply-stock-layout">
        <mat-card style="width: 100%; min-width: none;">
          <div class="table-wrap">
            <table mat-table [dataSource]="tableDataSource" class="mat-elevation-z8 demo-table">

              <!-- Substance Column -->
              <ng-container matColumnDef="substance">
                <th mat-header-cell *matHeaderCellDef>Substance</th>
                <td mat-cell *matCellDef="let row" [title]="row.substance" #substanceCell>
                  <div class="stacked-cell" style="max-width: 200px;">
                    <div class="primary-line" style="max-width: 200px;">{{ row.substance || '—' }}</div>
                  </div>
                  <div class="overlay-actions" #overlayActions>
                    <button mat-icon-button (click)="showStockDetails(row); $event.stopPropagation()"
                      matTooltip="View Details" class="round-icon-wrapper">
                      <mat-icon style="width: 16px; height: 16px; font-size: 16px;"
                        class="material-symbols-outlined">visibility</mat-icon>
                    </button>
                    @if (canEditStock()) {
                    <button mat-icon-button (click)="openUpdateDialog(row); $event.stopPropagation()"
                      matTooltip="Change Remaining" class="round-icon-wrapper">
                      <span class="material-symbols-outlined"
                        style="width: 16px; height: 16px; font-size: 16px;">edit</span>
                    </button>
                    }
                  </div>
                </td>
              </ng-container>

              <!-- Remaining Column -->
              <ng-container matColumnDef="totalRemaining">
                <th mat-header-cell *matHeaderCellDef>Remaining</th>
                <td mat-cell *matCellDef="let row">
                  <div class="stacked-cell">
                    @if(row.totalRemaining !== null && row.totalRemaining !== undefined){
                    <div class="primary-line">
                      <strong>{{ row.totalRemaining }}</strong>
                      @if (row.totalRemainingUnit) {
                      <span class="subtitle-text-color-2"> {{ row.totalRemainingUnit }}</span>
                      }
                    </div>
                    <div class="secondary-line">
                      @if (row.totalRemaining === 0 || row.packagesRemaining === 0) {
                      <span [appEmphasis]="'out-of-stock'" [fontSize]="'11px'" [showBorder]="false" [opacity]="0.85">out
                        of stock</span>
                      } @else if (row.lowStock) {
                      <span [appEmphasis]="'low'" [fontSize]="'11px'" [showBorder]="false" [opacity]="0.85"
                        [matTooltip]="row.lowStockLabel ?? 'Low stock threshold reached'">
                        {{ row.lowStockLabel ?? 'low stock' }}
                      </span>
                      } @else {
                      <span [appEmphasis]="'success'" [fontSize]="'11px'" [showBorder]="false" [opacity]="0.75">in
                        stock</span>
                      }
                    </div>
                    } @else {
                    <div class="secondary-line">
                      <span class="text-muted">N/A</span>
                    </div>
                    }
                  </div>
                </td>
              </ng-container>

              <!-- Price Column -->
              <ng-container matColumnDef="price">
                <th mat-header-cell *matHeaderCellDef>Price/Pricing</th>
                <td mat-cell *matCellDef="let row">
                  <div class="stacked-cell">
                    <div class="primary-line" style="font-weight: 500;">{{ row.priceDisplay }}</div>
                    @if (row.chargeItemDefinition) {
                    <div class="secondary-line" style="font-size: 11px; opacity: 0.7;">Linked to Pricing</div>
                    }
                  </div>
                </td>
              </ng-container>

              <!-- Received Column -->
              <ng-container matColumnDef="received">
                <th mat-header-cell *matHeaderCellDef>Received</th>
                <td mat-cell *matCellDef="let row">
                  <div class="stacked-cell">
                    @if(row.rawResource?.suppliedItem?.quantity?.unit &&
                    row.rawResource?.suppliedItem?.quantity?.value){
                    <div class="primary-line">
                      {{ row.rawResource.suppliedItem.quantity.value }} {{ row.rawResource.suppliedItem.quantity.unit }}
                    </div>
                    }
                    @if (row.packagesReceived != null) {
                    <div class="secondary-line">
                      {{ row.packagesReceived }} {{ row.packageName ?? '' }}.
                      @if(row.quantityPerPackage != null && row.quantityPerPackage > 0) {
                      ({{ row.quantityPerPackage }} {{ row.quantityPerPackageUnit }} / {{ row.packageName }})
                      }
                    </div>
                    }
                    @if((!row.rawResource?.suppliedItem?.quantity?.unit ||
                    !row.rawResource?.suppliedItem?.quantity?.value) && row.packagesReceived == null){
                    <div class="primary-line">N/A</div>
                    }
                  </div>
                </td>
              </ng-container>

              <!-- Updated Column -->
              <ng-container matColumnDef="lastUpdated">
                <th mat-header-cell *matHeaderCellDef>Updated</th>
                <td mat-cell *matCellDef="let row">
                  <div class="stacked-cell">
                    @if (row.lastUpdated) {
                    <div class="primary-line">{{ row.lastUpdated | date:'MMM d, yyyy' }}</div>
                    <div class="secondary-line">{{ row.lastUpdated | date:'h:mm a' }}</div>
                    } @else {
                    <div class="primary-line">—</div>
                    <div class="secondary-line"><span class="text-muted">N/A</span></div>
                    }
                  </div>
                </td>
              </ng-container>

              <!-- Status Column -->
              <ng-container matColumnDef="status">
                <th mat-header-cell *matHeaderCellDef>Status</th>
                <td mat-cell *matCellDef="let row">
                  @if (row.rawResource?.status) {
                  <div [appChips]="row.rawResource.status" [fontSize]="'14px'"></div>
                  } @else {
                  <span class="subtitle-text-color-2">N/A</span>
                  }
                </td>
              </ng-container>

              <!-- Actions Column -->
              <ng-container matColumnDef="actions">
                <th mat-header-cell *matHeaderCellDef>Actions</th>
                <td mat-cell *matCellDef="let row">
                  <button mat-icon-button [matMenuTriggerFor]="stockMenu" (click)="$event.stopPropagation()">
                    <mat-icon>more_vert</mat-icon>
                  </button>
                  <mat-menu #stockMenu="matMenu">
                    <button mat-menu-item (click)="showStockDetails(row); $event.stopPropagation()">
                      <mat-icon>visibility</mat-icon>
                      <span>View Details</span>
                    </button>
                    <button mat-menu-item (click)="openUpdateDialog(row); $event.stopPropagation()"
                      [disabled]="!canEditStock()">
                      <mat-icon>edit</mat-icon>
                      <span>Change Remaining</span>
                    </button>
                  </mat-menu>
                </td>
              </ng-container>

              <tr mat-header-row *matHeaderRowDef="displayedColumns" matHeaderRowDefSticky="true"></tr>
              <tr mat-row *matRowDef="let row; columns: displayedColumns" (click)="selectRow(row)"
                style="cursor:pointer">
              </tr>
            </table>
          </div>

          <mat-paginator [length]="total$ | async" [pageSize]="10" (page)="onPageChange($event)">
          </mat-paginator>
        </mat-card>
      </div>
      }
    </div>
    }
  </mat-card-content>
</mat-card>